/** Repo-owned humanoid animation source format. Runtime renderers may compile
 * bone tracks to their native clip type; constraints/events remain gameplay data. */

export type AnimVec3 = [number, number, number];
export type AnimQuat = [number, number, number, number];
export type HumanoidRootMode = "locked" | "in-place" | "motion";
export type HumanoidInterpolation = "linear" | "step";

export interface HumanoidQuaternionKey {
  time: number;
  value: AnimQuat;
  interpolation?: HumanoidInterpolation;
}

export interface HumanoidVectorKey {
  time: number;
  value: AnimVec3;
  interpolation?: HumanoidInterpolation;
}

export interface HumanoidScalarKey {
  time: number;
  value: number;
  interpolation?: HumanoidInterpolation;
}

export interface HumanoidBoneTrack {
  rotation?: HumanoidQuaternionKey[];
  position?: HumanoidVectorKey[];
}

/** A semantic runtime target. `context:*` resolves against the object supplied
 * by an interaction (vehicle, ladder, chair, console); `rig:*` targets a bone. */
export interface HumanoidConstraintTrack {
  id: string;
  effector: string;
  target: string;
  /** Animated offset/goal in the target frame. For `context:origin` this is the
   * complete interaction-local goal; for a named socket it is an offset. */
  targetPosition?: HumanoidVectorKey[];
  /** Bend direction in avatar-local space (elbow/knee pole), normalized at use. */
  pole?: AnimVec3;
  positionWeight: HumanoidScalarKey[];
  rotationWeight?: HumanoidScalarKey[];
}

export interface HumanoidAnimationEvent {
  time: number;
  type: string;
  payload?: Readonly<Record<string, unknown>>;
}

/** Development preview layout. Gameplay ignores this block; keeping it in the
 * editable source lets an author reopen the exact multi-prop interaction scene. */
export interface HumanoidPreviewContext {
  id: string;
  kind: "vehicle" | "item";
  sourceId: string;
  position: AnimVec3;
  rotation: AnimQuat;
  scale: AnimVec3;
}

export interface HumanoidAnimationAssetV1 {
  version: 1;
  id: string;
  duration: number;
  loop: boolean;
  sampleRate: number;
  rootMode: HumanoidRootMode;
  tags: string[];
  boneTracks: Record<string, HumanoidBoneTrack>;
  constraints?: HumanoidConstraintTrack[];
  events?: HumanoidAnimationEvent[];
  previewContexts?: HumanoidPreviewContext[];
}

const ID = /^[a-z0-9][a-z0-9._/-]*$/;
const CONTEXT_ID = /^[a-z0-9][a-z0-9._-]*$/;

function finiteTuple(value: unknown, size: number): value is number[] {
  return Array.isArray(value) && value.length === size
    && value.every((n) => typeof n === "number" && Number.isFinite(n));
}

function checkTimes(
  label: string,
  keys: readonly { time: number; value: unknown }[],
  duration: number,
  tupleSize: number,
  issues: string[],
): void {
  let previous = -Infinity;
  keys.forEach((key, index) => {
    if (!Number.isFinite(key.time) || key.time < 0 || key.time > duration) {
      issues.push(`${label}[${index}].time must be within 0..duration`);
    }
    if (key.time <= previous) issues.push(`${label} key times must be strictly increasing`);
    previous = key.time;
    if (!finiteTuple(key.value, tupleSize)) issues.push(`${label}[${index}].value must contain ${tupleSize} finite numbers`);
  });
}

function checkScalarTimes(
  label: string,
  keys: readonly HumanoidScalarKey[],
  duration: number,
  issues: string[],
): void {
  let previous = -Infinity;
  keys.forEach((key, index) => {
    if (!Number.isFinite(key.time) || key.time < 0 || key.time > duration) issues.push(`${label}[${index}].time must be within 0..duration`);
    if (key.time <= previous) issues.push(`${label} key times must be strictly increasing`);
    previous = key.time;
    if (!Number.isFinite(key.value) || key.value < 0 || key.value > 1) issues.push(`${label}[${index}].value must be within 0..1`);
  });
}

/** Validate untrusted/imported authoring data without assuming its shape. */
export function humanoidAnimationIssues(value: unknown): string[] {
  const issues: string[] = [];
  if (!value || typeof value !== "object") return ["asset must be an object"];
  const asset = value as Partial<HumanoidAnimationAssetV1>;
  if (asset.version !== 1) issues.push("version must be 1");
  if (typeof asset.id !== "string" || !ID.test(asset.id)) issues.push("id must be a lowercase asset id");
  if (typeof asset.duration !== "number" || !Number.isFinite(asset.duration) || asset.duration <= 0 || asset.duration > 120) {
    issues.push("duration must be > 0 and <= 120 seconds");
  }
  if (typeof asset.loop !== "boolean") issues.push("loop must be boolean");
  if (typeof asset.sampleRate !== "number" || !Number.isInteger(asset.sampleRate) || asset.sampleRate < 1 || asset.sampleRate > 240) {
    issues.push("sampleRate must be an integer within 1..240");
  }
  if (asset.rootMode !== "locked" && asset.rootMode !== "in-place" && asset.rootMode !== "motion") {
    issues.push("rootMode must be locked, in-place, or motion");
  }
  if (!Array.isArray(asset.tags) || asset.tags.some((tag) => typeof tag !== "string")) issues.push("tags must be strings");
  const duration = typeof asset.duration === "number" && Number.isFinite(asset.duration) ? asset.duration : 0;
  if (!asset.boneTracks || typeof asset.boneTracks !== "object" || Array.isArray(asset.boneTracks)) {
    issues.push("boneTracks must be an object");
  } else {
    for (const [bone, track] of Object.entries(asset.boneTracks)) {
      if (!bone || !track || typeof track !== "object") {
        issues.push(`boneTracks.${bone || "<empty>"} must be an object`);
        continue;
      }
      if (track.rotation) {
        if (!Array.isArray(track.rotation)) issues.push(`boneTracks.${bone}.rotation must be an array`);
        else {
          checkTimes(`boneTracks.${bone}.rotation`, track.rotation, duration, 4, issues);
          track.rotation.forEach((key, index) => {
            if (!finiteTuple(key.value, 4)) return;
            // Math.sqrt is exactly rounded (portable); Math.hypot is banned by pmath-ban.
            const length = Math.sqrt(key.value.reduce((sum, v) => sum + v * v, 0));
            if (Math.abs(length - 1) > 1e-3) issues.push(`boneTracks.${bone}.rotation[${index}] must be normalized`);
          });
        }
      }
      if (track.position) {
        if (!Array.isArray(track.position)) issues.push(`boneTracks.${bone}.position must be an array`);
        else checkTimes(`boneTracks.${bone}.position`, track.position, duration, 3, issues);
      }
    }
  }
  if (asset.events !== undefined) {
    if (!Array.isArray(asset.events)) issues.push("events must be an array");
    else {
      let previous = -Infinity;
      asset.events.forEach((event, index) => {
        if (!event || typeof event.type !== "string" || !event.type.trim()) issues.push(`events[${index}].type is required`);
        if (!event || !Number.isFinite(event.time) || event.time < 0 || event.time > duration) issues.push(`events[${index}].time must be within 0..duration`);
        if (event && event.time < previous) issues.push("events must be sorted by time");
        if (event) previous = event.time;
      });
    }
  }
  if (asset.constraints !== undefined) {
    if (!Array.isArray(asset.constraints)) issues.push("constraints must be an array");
    else {
      const ids = new Set<string>();
      asset.constraints.forEach((constraint, index) => {
        const label = `constraints[${index}]`;
        if (!constraint || typeof constraint.id !== "string" || !constraint.id) issues.push(`${label}.id is required`);
        else if (ids.has(constraint.id)) issues.push(`constraint id ${constraint.id} is duplicated`);
        else ids.add(constraint.id);
        if (!constraint || typeof constraint.effector !== "string" || !constraint.effector) issues.push(`${label}.effector is required`);
        if (!constraint || typeof constraint.target !== "string" || !/^(context|rig):/.test(constraint.target)) issues.push(`${label}.target must start with context: or rig:`);
        if (constraint?.targetPosition !== undefined) {
          if (!Array.isArray(constraint.targetPosition)) issues.push(`${label}.targetPosition must be an array`);
          else checkTimes(`${label}.targetPosition`, constraint.targetPosition, duration, 3, issues);
        }
        if (constraint?.pole !== undefined && !finiteTuple(constraint.pole, 3)) issues.push(`${label}.pole must contain 3 finite numbers`);
        if (!constraint || !Array.isArray(constraint.positionWeight)) issues.push(`${label}.positionWeight must be an array`);
        else checkScalarTimes(`${label}.positionWeight`, constraint.positionWeight, duration, issues);
        if (constraint?.rotationWeight !== undefined) {
          if (!Array.isArray(constraint.rotationWeight)) issues.push(`${label}.rotationWeight must be an array`);
          else checkScalarTimes(`${label}.rotationWeight`, constraint.rotationWeight, duration, issues);
        }
      });
    }
  }
  if (asset.previewContexts !== undefined) {
    if (!Array.isArray(asset.previewContexts)) issues.push("previewContexts must be an array");
    else {
      const ids = new Set<string>();
      asset.previewContexts.forEach((context, index) => {
        const label = `previewContexts[${index}]`;
        if (!context || typeof context.id !== "string" || !CONTEXT_ID.test(context.id)) issues.push(`${label}.id must be a lowercase instance id without slashes`);
        else if (ids.has(context.id)) issues.push(`preview context id ${context.id} is duplicated`);
        else ids.add(context.id);
        if (!context || (context.kind !== "vehicle" && context.kind !== "item")) issues.push(`${label}.kind must be vehicle or item`);
        if (!context || typeof context.sourceId !== "string" || !context.sourceId) issues.push(`${label}.sourceId is required`);
        if (!context || !finiteTuple(context.position, 3)) issues.push(`${label}.position must contain 3 finite numbers`);
        if (!context || !finiteTuple(context.rotation, 4)) issues.push(`${label}.rotation must contain 4 finite numbers`);
        else if (Math.abs(Math.sqrt(context.rotation.reduce((sum: number, v: number) => sum + v * v, 0)) - 1) > 1e-3) issues.push(`${label}.rotation must be normalized`);
        if (!context || !finiteTuple(context.scale, 3) || context.scale.some((axis) => axis <= 0)) issues.push(`${label}.scale must contain 3 positive finite numbers`);
      });
    }
  }
  return issues;
}

export function isHumanoidAnimationAsset(value: unknown): value is HumanoidAnimationAssetV1 {
  return humanoidAnimationIssues(value).length === 0;
}
